import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import {
  Box, Typography, Table, TableHead, TableRow, TableCell, Button, Stack, Chip, IconButton, Tooltip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Grid, Card, CardContent, Tabs, Tab,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import {
  ADMIN_ALL_NOTIFICATIONS_QUERY,
  CREATE_NOTIFICATION_MUTATION,
  BROADCAST_NOTIFICATION_MUTATION,
} from '../graphql/queries';

const TYPE_COLOR = { info: 'info', warning: 'warning', error: 'error', success: 'success', reminder: 'primary', emergency: 'error' };
const TYPES      = ['info','reminder','warning','emergency','success'];
const ROLES      = ['','admin','user','vendor'];

const EMPTY_SEND    = { userId: '', type: 'info', title: '', message: '' };
const EMPTY_BROADCAST = { type: 'info', title: '', message: '', targetRole: '' };

export default function AdminNotificationsPage() {
  const [tab, setTab]                 = useState(0);
  const [sendForm, setSendForm]       = useState(EMPTY_SEND);
  const [bcastForm, setBcastForm]     = useState(EMPTY_BROADCAST);
  const [snack, setSnack]             = useState({ open: false, msg: '', ok: true });

  const { data, loading, refetch } = useQuery(ADMIN_ALL_NOTIFICATIONS_QUERY, { fetchPolicy: 'cache-and-network' });

  const toast = (msg, ok = true) => setSnack({ open: true, msg, ok });

  const [sendNotif, { loading: sending }] = useMutation(CREATE_NOTIFICATION_MUTATION, {
    onCompleted: () => { setSendForm(EMPTY_SEND); refetch(); toast('Notification sent.'); },
    onError: e => toast(e.message, false),
  });
  const [broadcast, { loading: broadcasting }] = useMutation(BROADCAST_NOTIFICATION_MUTATION, {
    onCompleted: () => { setBcastForm(EMPTY_BROADCAST); refetch(); toast('Broadcast sent.'); },
    onError: e => toast(e.message, false),
  });

  const notifications = data?.allNotifications || [];

  const handleSend = () => {
    if (!sendForm.userId.trim() || !sendForm.title.trim() || !sendForm.message.trim()) return;
    sendNotif({ variables: sendForm });
  };
  const handleBroadcast = () => {
    if (!bcastForm.title.trim() || !bcastForm.message.trim()) return;
    broadcast({ variables: { ...bcastForm, targetRole: bcastForm.targetRole || null } });
  };

  const read    = notifications.filter(n => n.isRead).length;
  const unread  = notifications.filter(n => !n.isRead).length;

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Notifications</Typography>
          <Typography variant="body2" color="text.secondary">Send and manage notifications for all users</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Chip icon={<NotificationsActiveIcon />} label={`${unread} unread`} color="error" />
          <Chip label={`${read} read`} color="default" />
        </Stack>
      </Stack>

      {snack.open && (
        <Alert severity={snack.ok ? 'success' : 'error'} onClose={() => setSnack(s => ({ ...s, open: false }))} sx={{ mb: 2 }}>
          {snack.msg}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All Notifications" />
        <Tab label="Send to User" />
        <Tab label="Broadcast" />
      </Tabs>

      {/* Tab 0 — All notifications list */}
      {tab === 0 && (
        loading ? <CircularProgress /> : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>User ID</TableCell>
                <TableCell>Sent By</TableCell>
                <TableCell>Read</TableCell>
                <TableCell>At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {notifications.map(n => (
                <TableRow key={n.id} hover sx={{ opacity: n.isRead ? 0.7 : 1 }}>
                  <TableCell><Chip label={n.type || 'info'} size="small" color={TYPE_COLOR[n.type] || 'default'} /></TableCell>
                  <TableCell><strong>{n.title}</strong></TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>{n.message}</TableCell>
                  <TableCell>{n.userId}</TableCell>
                  <TableCell>{n.creator?.email || '—'}</TableCell>
                  <TableCell><Chip label={n.isRead ? 'Read' : 'Unread'} size="small" color={n.isRead ? 'default' : 'error'} /></TableCell>
                  <TableCell>{n.createdAt ? new Date(n.createdAt).toLocaleString() : '—'}</TableCell>
                </TableRow>
              ))}
              {notifications.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">No notifications found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        )
      )}

      {/* Tab 1 — Send to specific user */}
      {tab === 1 && (
        <Card variant="outlined" sx={{ maxWidth: 500 }}>
          <CardContent>
            <Typography variant="h6" mb={2}>Send Notification to User</Typography>
            <Stack spacing={2}>
              <TextField label="User ID" value={sendForm.userId} onChange={e => setSendForm(f => ({ ...f, userId: e.target.value }))} required fullWidth helperText="Enter the numeric User ID" />
              <TextField select label="Type" value={sendForm.type} onChange={e => setSendForm(f => ({ ...f, type: e.target.value }))} fullWidth>
                {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField label="Title" value={sendForm.title} onChange={e => setSendForm(f => ({ ...f, title: e.target.value }))} required fullWidth />
              <TextField label="Message" value={sendForm.message} onChange={e => setSendForm(f => ({ ...f, message: e.target.value }))} required multiline rows={3} fullWidth />
              <Button variant="contained" onClick={handleSend} disabled={sending} startIcon={<SendIcon />}>
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Tab 2 — Broadcast */}
      {tab === 2 && (
        <Card variant="outlined" sx={{ maxWidth: 500 }}>
          <CardContent>
            <Typography variant="h6" mb={2}>Broadcast to All Users</Typography>
            <Stack spacing={2}>
              <TextField select label="Target Role (optional)" value={bcastForm.targetRole} onChange={e => setBcastForm(f => ({ ...f, targetRole: e.target.value }))} fullWidth>
                {ROLES.map(r => <MenuItem key={r} value={r}>{r || '— All users —'}</MenuItem>)}
              </TextField>
              <TextField select label="Type" value={bcastForm.type} onChange={e => setBcastForm(f => ({ ...f, type: e.target.value }))} fullWidth>
                {TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              <TextField label="Title" value={bcastForm.title} onChange={e => setBcastForm(f => ({ ...f, title: e.target.value }))} required fullWidth />
              <TextField label="Message" value={bcastForm.message} onChange={e => setBcastForm(f => ({ ...f, message: e.target.value }))} required multiline rows={3} fullWidth />
              <Button variant="contained" color="warning" onClick={handleBroadcast} disabled={broadcasting} startIcon={<NotificationsActiveIcon />}>
                {broadcasting ? 'Broadcasting…' : 'Broadcast to All'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
